import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { useT } from '../i18n';
import {
  SELF_HOSTED_DEACTIVATE,
  SELF_HOSTED_INTRO,
  SELF_HOSTED_KEY_ONCE,
  SELF_HOSTED_ROTATE,
  SELF_HOSTED_RULES,
  SELF_HOSTED_UPSELL,
} from '../i18n/selfHosted';
import XpnsrPageHeader from './XpnsrPageHeader';
import ConfirmDialog from './ConfirmDialog';
import SelfHostedInstall from './SelfHostedInstall';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, Copy, Key, Server, ShieldCheck } from 'lucide-react';

/*
  Enterprise: the licence key for self-hosted installs and the servers using
  it. The key is shown once, right after it is issued or rotated; the server
  keeps only its hash. See docs/LICENSING.md.
*/

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_e) {
    return String(value);
  }
}

export default function SelfHosted() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState('');
  const [pending, setPending] = useState(null);

  const load = async () => {
    setFailed(false);
    try {
      const r = await axios.get(`${API}/self-hosted`);
      setData(r.data || {});
    } catch (_e) {
      setFailed(true);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const issueKey = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/self-hosted/license`);
      setFreshKey((r.data && r.data.licence_key) || '');
      setData(r.data || {});
      toast.success(t('Licence key issued'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const deactivate = async (instance) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/self-hosted/instances/${encodeURIComponent(instance.instance_id)}/deactivate`
      );
      setData(r.data || {});
      toast.success(t('Server deactivated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(freshKey);
      toast.success(t('Copied'));
    } catch (_e) {
      toast.error(t('Action failed'));
    }
  };

  if (failed) {
    return (
      <div className="px-8 py-10 max-w-5xl">
        <p className="text-sm text-muted-foreground mb-3">{t('Could not load this page.')}</p>
        <Button variant="outline" onClick={load}>{t('Try again')}</Button>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-muted-foreground">{t('Loading…')}</div>;

  const days = data.lease_days || 7;
  const licence = data.licence;
  const instances = data.instances || [];
  const active = instances.filter((i) => i.status === 'active');
  const slots = data.slots || {};
  const used = (role) => active.filter((i) => i.role === role).length;

  return (
    <div className="px-8 py-10 max-w-5xl" data-testid="self-hosted-page">
      <XpnsrPageHeader kicker={t('Enterprise')} title={t('Self-hosted')} subtitle={t(SELF_HOSTED_INTRO)} />
      <div className="mb-6" />

      {!data.entitled && (
        <Card className="mb-6 border-primary/40">
          <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-start gap-3 text-sm">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-primary shrink-0" />
              <span>{t(SELF_HOSTED_UPSELL)}</span>
            </div>
            <Button asChild>
              <Link to="/billing">{t('See plans')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {data.entitled && !data.signing_ready && (
        <Card className="mb-6 border-amber-500/40">
          <CardContent className="py-4 flex items-center gap-3 text-sm text-muted-foreground">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            {t('Licensing is temporarily unavailable. Try again later.')}
          </CardContent>
        </Card>
      )}

      {data.entitled && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Key className="w-4 h-4" /> {t('Licence key')}
              {licence && (
                <Badge variant="outline">{licence.status === 'active' ? t('Active') : t('Revoked')}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {licence ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <span className="font-mono">{licence.key_hint}</span>
                <span className="text-muted-foreground">
                  {t('Issued')}: {fmtDateTime(licence.rotated_at || licence.created_at)}
                </span>
              </div>
            ) : null}
            {freshKey ? (
              <div className="rounded-lg border border-primary/40 bg-primary/[0.04] p-4 space-y-3" data-testid="fresh-licence-key">
                <div className="flex flex-wrap items-center gap-3">
                  <code className="font-mono text-sm break-all select-all">{freshKey}</code>
                  <Button size="sm" variant="outline" onClick={copyKey}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('Copy')}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t(SELF_HOSTED_KEY_ONCE)}</p>
              </div>
            ) : null}
            <Button
              variant={licence ? 'outline' : 'default'}
              disabled={busy || !data.signing_ready}
              onClick={() => (licence ? setPending({ kind: 'rotate' }) : issueKey())}
              data-testid="licence-issue"
            >
              {licence ? t('Rotate key') : t('Issue licence key')}
            </Button>
          </CardContent>
        </Card>
      )}

      {(data.entitled || instances.length > 0) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base flex-wrap">
              <Server className="w-4 h-4" /> {t('Servers')}
              <Badge variant="outline">
                {t('Production')} {used('production')}/{slots.production ?? 1}
              </Badge>
              <Badge variant="outline">
                {t('Test')} {used('test')}/{slots.test ?? 1}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {instances.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No servers activated yet.')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {instances.map((i) => (
                  <li key={i.instance_id} className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <Badge variant="outline">{i.role === 'test' ? t('Test') : t('Production')}</Badge>
                    <span className="font-medium">{i.hostname || i.instance_id}</span>
                    {i.version ? (
                      <span className="text-muted-foreground">
                        {t('Version')} {i.version}
                      </span>
                    ) : null}
                    <span className="text-muted-foreground">
                      {t('Last seen')}: {fmtDateTime(i.last_seen_at)}
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                      {i.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setPending({ kind: 'deactivate', instance: i })}
                          data-testid={`deactivate-${i.instance_id}`}
                        >
                          {t('Deactivate')}
                        </Button>
                      ) : (
                        <Badge variant="outline">{t('Deactivated')}</Badge>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              {t('Transfers left (30 days)')}: {data.transfers_left ?? 0}
            </p>
          </CardContent>
        </Card>
      )}

      {data.entitled && (
        <SelfHostedInstall licenceKey={freshKey} keyHint={licence ? licence.key_hint : ''} />
      )}

      {data.entitled && (
        <Card>
          <CardContent className="py-5 space-y-3 text-sm text-muted-foreground">
            <p>
              {t(SELF_HOSTED_RULES, {
                days,
                clicks: Number(data.test_daily_click_cap || 0).toLocaleString(),
              })}
            </p>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={pending && pending.kind === 'rotate' ? t('Rotate licence key?') : t('Deactivate this server?')}
        description={
          pending && pending.kind === 'rotate'
            ? t(SELF_HOSTED_ROTATE, { days })
            : t(SELF_HOSTED_DEACTIVATE, { days })
        }
        confirmLabel={pending && pending.kind === 'rotate' ? t('Rotate key') : t('Deactivate')}
        loading={busy}
        onConfirm={() => {
          if (!pending) return;
          if (pending.kind === 'rotate') issueKey();
          else deactivate(pending.instance);
        }}
      />
    </div>
  );
}
